// Fetches the three editable lookup lists once per session and exposes them to
// every component, so no component fetches them on its own. Provides active-only
// options for dropdowns, full label resolvers (so existing rows referencing an
// inactive/relabeled value still render), and a reload() the Settings screen
// calls after any edit.
import { createContext, useContext } from 'react';
import useAsync from '../hooks/useAsync';
import { settingsApi } from '../api';

const LookupsContext = createContext(null);

const EMPTY = {
  revenueStreams: [], paymentMethods: [], paymentTerms: [],
  leadSources: [], expenseCategories: [], incomeCategories: [],
  outlets: [], documentCategories: [],
};

const toOptions = (rows) => rows.filter((r) => r.is_active).map((r) => ({ value: r.code, label: r.label_he }));
const toLabel = (rows, code) => {
  const row = rows.find((r) => r.code === code);
  return row ? row.label_he : code;
};

// Active options plus the record's current value if that value is now inactive,
// so opening an edit form never silently changes a stored code.
const optionsFor = (rows, currentCode) => {
  const options = toOptions(rows);
  if (currentCode && !options.some((o) => o.value === currentCode)) {
    const row = rows.find((r) => r.code === currentCode);
    if (row) options.push({ value: row.code, label: row.label_he });
  }
  return options;
};

// Expense-category options for one screen: keep only categories whose context is
// this screen's ('job' or 'office') or 'both', then apply the standard active +
// keep-current-value rule so an edit never drops a stored (now inactive) category.
const categoryOptionsFor = (rows, context, currentCode) => {
  const scoped = rows.filter((r) => r.context === context || r.context === 'both' || r.code === currentCode);
  return optionsFor(scoped, currentCode);
};

export const LookupsProvider = ({ children }) => {
  const { data, loading, reload } = useAsync(() => settingsApi.getAll());
  const lists = data || EMPTY;

  const value = {
    loading,
    reload,
    raw: lists,
    streamOptions: toOptions(lists.revenueStreams),
    methodOptions: toOptions(lists.paymentMethods),
    termOptions: toOptions(lists.paymentTerms),
    sourceOptions: toOptions(lists.leadSources),
    outletOptions: toOptions(lists.outlets),
    docCategoryOptions: toOptions(lists.documentCategories),
    streamOptionsFor: (code) => optionsFor(lists.revenueStreams, code),
    methodOptionsFor: (code) => optionsFor(lists.paymentMethods, code),
    termOptionsFor: (code) => optionsFor(lists.paymentTerms, code),
    sourceOptionsFor: (code) => optionsFor(lists.leadSources, code),
    // A document filed in a now-inactive drawer must still be editable, so the
    // stored value is kept in the list even when it is no longer offered.
    docCategoryOptionsFor: (code) => optionsFor(lists.documentCategories, code),
    // context is 'job' or 'office' — which expense screen is asking.
    categoryOptionsFor: (context, code) => categoryOptionsFor(lists.expenseCategories, context, code),
    // What a recorded income line was. Unscoped — income is recorded from one screen,
    // so there is no context to narrow by, only the standard keep-the-stored-value rule.
    incomeCategoryOptions: toOptions(lists.incomeCategories),
    incomeCategoryOptionsFor: (code) => optionsFor(lists.incomeCategories, code),
    incomeCategoryLabel: (code) => toLabel(lists.incomeCategories, code),
    streamLabel: (code) => toLabel(lists.revenueStreams, code),
    methodLabel: (code) => toLabel(lists.paymentMethods, code),
    termLabel: (code) => toLabel(lists.paymentTerms, code),
    sourceLabel: (code) => toLabel(lists.leadSources, code),
    categoryLabel: (code) => toLabel(lists.expenseCategories, code),
    outletLabel: (code) => toLabel(lists.outlets, code),
    docCategoryLabel: (code) => toLabel(lists.documentCategories, code),
  };

  return <LookupsContext.Provider value={value}>{children}</LookupsContext.Provider>;
};

export const useLookups = () => useContext(LookupsContext);
